import { ProductCard, ProductCardSkeleton } from "@/components/ui/ProductCard";
import { ChevronDown } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  apiFetch,
  bulkDeleteProducts,
  bulkSetProductsStatus,
  bulkSetVariantsAnnouncement,
  bulkSetVariantsUnavailable,
  listAnnouncements,
  type Announcement,
} from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useVariantImagesMap } from "@/lib/variantImages";
import { AnnouncementPicker } from "@/components/ui/AnnouncementPicker";
import { VariantAvailabilityPicker } from "@/components/ui/VariantAvailabilityPicker";
import { VariantAnnouncementModal } from "@/components/ui/VariantAnnouncementModal";
import { MultiSelectFilter } from "@/components/ui/MultiSelectFilter";
import { SelectionCircle } from "@/components/ui/SelectionCircle";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useLanguage } from "@/hooks/useLanguage";
import { pickLocalized } from "@/lib/localized";
import { queryClient } from "@/lib/queryClient";
import {
  clearProductOrigin,
  getProductListSnapshot,
  rememberProductOrigin,
} from "@/lib/productNavigation";

type Product = {
  id: string;
  name: string;
  name_en: string | null;
  slug: string;
  brand: { name: string };
  status: { name: string };
  announcement: {
    id: string;
    message: string;
    message_en: string | null;
    text_color: string | null;
    background_color: string | null;
  } | null;
  variant_ids: number[];
  variant_skus: string[];
  variant_names?: string[];
  variant_names_en?: string[];
  variant_announcements?: {
    id: number;
    announcement: Announcement | null;
    is_unavailable?: boolean;
  }[];
};

const STATUS_TABS = [
  { value: "published", labelKey: "products.statusPublished" },
  { value: "draft", labelKey: "products.statusDraft" },
  { value: "hidden", labelKey: "products.statusHidden" },
] as const;

type StatusFilter = (typeof STATUS_TABS)[number]["value"];

type Brand = { id: number; name: string };
type Category = { id: number; name: string; name_en: string };
type OptionsPage<T> = { items: T[] };

type Page = {
  items: Product[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
};

type ProductListSnapshot = {
  products: Product[];
  total: number;
  hasMore: boolean;
  offset: number;
  statusFilter: StatusFilter;
  search: string;
  selectedBrandIds: number[];
  selectedCategoryIds: number[];
  selectedAnnouncementIds: string[];
  featuredOnly: boolean;
  unavailableOnly: boolean;
  brands: Brand[];
  categories: Category[];
  announcements: Announcement[];
  optionsLoaded: boolean;
  filtersReady: boolean;
  scrollY: number;
  visibleVariantIndexes: Record<string, number>;
};

const LIMIT = 20;
const INITIAL_SKELETON_COUNT = 10;

// Lets a link like /products?brand=Herbacol match a DB brand/category name
// even if casing or accents differ (e.g. "Champú" vs "champu").
const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

// Touch scrolling (flick/momentum) covers far more pixels per second than a
// mouse wheel or trackpad, so the same lookahead margin leaves much less
// real time for the next page's images to fetch and decode before the user
// scrolls into view. Coarse pointers need a much bigger margin to
// compensate - big enough that a percentage (capped at 100% of the viewport
// height) can't express it, so touch gets a flat px value instead.
const SCROLL_THRESHOLD = typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches ? "2000px" : 0.2;

// react-infinite-scroll-component only re-checks its sentinel when the
// IntersectionObserver reports a fresh enter/exit - not merely "still
// intersecting". If a page loads while the sentinel is already deep inside
// the threshold margin (e.g. the user scrolled to the bottom before the
// first page arrived) and the newly appended content isn't tall enough to
// push the sentinel out of that margin and back in, the browser never fires
// the callback again and pagination silently stalls. This mirrors the same
// px-vs-percent math to compute "are we still inside the margin right now?"
// as a backstop that calls fetchMore directly instead of waiting on a
// crossing event that may never come.
function getScrollMarginPx(threshold: number | string): number {
  if (typeof threshold === "string" && threshold.endsWith("px")) return parseFloat(threshold);
  const fraction = typeof threshold === "number" ? threshold : 0.8;
  return window.innerHeight * (1 - fraction);
}

export default function Products() {
  const { t } = useTranslation();
  usePageTitle(t("products.title"));
  const { currentLanguage } = useLanguage();
  const { user, loading: authLoading } = useAuth();
  const isSuperuser = !!user?.is_superuser;
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const currentPath = `${location.pathname}${location.search}`;
  const restoredSnapshotRef = useRef(
    getProductListSnapshot<ProductListSnapshot>(currentPath)
  );
  const restoredSnapshot = restoredSnapshotRef.current;
  const [products, setProducts] = useState<Product[]>(restoredSnapshot?.products ?? []);
  const [total, setTotal] = useState(restoredSnapshot?.total ?? 0);
  const [hasMore, setHasMore] = useState(restoredSnapshot?.hasMore ?? true);
  const [isInitialLoading, setIsInitialLoading] = useState(!restoredSnapshot);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(restoredSnapshot?.statusFilter ?? "published");
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkActionPending, setBulkActionPending] = useState(false);
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  const [pendingAnnouncementId, setPendingAnnouncementId] = useState<string | null>(null);
  // Non-null switches the modal from announcements to availability.
  const [pendingUnavailable, setPendingUnavailable] = useState<boolean | null>(null);
  const [visibleVariantIndexes, setVisibleVariantIndexes] = useState<Record<string, number>>(
    restoredSnapshot?.visibleVariantIndexes ?? {}
  );
  const [search, setSearch] = useState(restoredSnapshot?.search ?? "");
  const [selectedBrandIds, setSelectedBrandIds] = useState<Set<number>>(
    new Set(restoredSnapshot?.selectedBrandIds ?? [])
  );
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<number>>(
    new Set(restoredSnapshot?.selectedCategoryIds ?? [])
  );
  const [selectedAnnouncementIds, setSelectedAnnouncementIds] = useState<Set<string>>(
    new Set(restoredSnapshot?.selectedAnnouncementIds ?? [])
  );
  const [featuredOnly, setFeaturedOnly] = useState(restoredSnapshot?.featuredOnly ?? false);
  const [unavailableOnly, setUnavailableOnly] = useState(restoredSnapshot?.unavailableOnly ?? false);
  const [brands, setBrands] = useState<Brand[]>(restoredSnapshot?.brands ?? []);
  const [categories, setCategories] = useState<Category[]>(restoredSnapshot?.categories ?? []);
  const [announcements, setAnnouncements] = useState<Announcement[]>(restoredSnapshot?.announcements ?? []);
  const [optionsLoaded, setOptionsLoaded] = useState(restoredSnapshot?.optionsLoaded ?? false);
  const [taxonomyVersion, setTaxonomyVersion] = useState(0);

  // Unavailable variants live in a collapsible list below the infinite scroll
  // (products shown with only their unavailable variants); it only loads once
  // the main list has been exhausted.
  const [unavailableProducts, setUnavailableProducts] = useState<Product[]>([]);
  const [unavailableOpen, setUnavailableOpen] = useState(false);

  const offsetRef = useRef(restoredSnapshot?.offset ?? 0);
  const loadingRef = useRef(false);
  const requestIdRef = useRef(0);
  // A link like the home page's brand tiles lands here with ?brand=... -
  // known synchronously from the URL, before the brand/category lists have
  // even loaded. Read once on mount so the very first fetch can wait for
  // that param to resolve into an id instead of firing unfiltered first and
  // flashing the full catalog before the filtered list replaces it.
  const hasUrlFilterParamRef = useRef(
    restoredSnapshot ? false : !!(searchParams.get("brand") || searchParams.get("category"))
  );
  // Gates the initial reload below. Starts true when there's no brand/
  // category param to resolve (nothing to wait for); when there is, it
  // flips to true in the same effect/render as the resolved filter below,
  // so the reload effect sees the final brand/category id and its "wait"
  // state change together instead of firing once unfiltered in between.
  const [filtersReady, setFiltersReady] = useState(
    restoredSnapshot?.filtersReady ?? !hasUrlFilterParamRef.current
  );
  // True while the debounced reload effect below has a reload scheduled but
  // not yet fired. Without this, the infinite-scroll backstop effect (which
  // fetches immediately, not debounced) can win the race and fetch first -
  // rendering a page of results that reloadProducts() then immediately
  // throws away and refetches once its timeout fires, reading as a
  // double-load flicker.
  const reloadPendingRef = useRef(false);
  const restoringInitialStateRef = useRef(!!restoredSnapshot);

  useEffect(() => {
    const onTaxonomyChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ kind: "brand" | "category"; deletedId?: number }>).detail;
      if (detail?.deletedId && detail.kind === "brand") {
        setSelectedBrandIds(prev => {
          const next = new Set(prev);
          next.delete(detail.deletedId!);
          return next;
        });
      }
      if (detail?.deletedId && detail.kind === "category") {
        setSelectedCategoryIds(prev => {
          const next = new Set(prev);
          next.delete(detail.deletedId!);
          return next;
        });
      }
      setTaxonomyVersion(version => version + 1);
    };
    window.addEventListener("taxonomy-changed", onTaxonomyChanged);
    return () => window.removeEventListener("taxonomy-changed", onTaxonomyChanged);
  }, []);

  const toggleSelected = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const toggleSelectMode = () => {
    setSelectMode(prev => !prev);
    clearSelection();
  };

  useEffect(() => {
    if (!selectMode || announcementModalOpen) return;

    const leaveSelectMode = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setSelectMode(false);
      setSelectedIds(new Set());
    };

    document.addEventListener("keydown", leaveSelectMode);
    return () => document.removeEventListener("keydown", leaveSelectMode);
  }, [selectMode, announcementModalOpen]);

  const runBulkAction = async (action: () => Promise<void>) => {
    setBulkActionPending(true);
    setError(null);
    try {
      await action();
      clearSelection();
      clearProductOrigin();
      reloadProducts();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : t("products.bulkActionFailed"));
      return false;
    } finally {
      setBulkActionPending(false);
    }
  };

  const onBulkDelete = () => {
    const ids = [...selectedIds];
    if (!ids.length || !confirm(t("products.confirmDeleteMany", { count: ids.length }))) return;
    runBulkAction(() => bulkDeleteProducts(ids));
  };

  const onBulkSetStatus = (status: StatusFilter) => {
    const ids = [...selectedIds];
    if (!ids.length) return;

    // Drafts can be missing variants; the backend rejects the whole batch
    // atomically if any selected product doesn't meet that invariant, so
    // warn up front rather than let it fail silently mid-batch.
    if (status === "published" && statusFilter === "draft") {
      const proceed = confirm(t("products.confirmPublishDrafts", { count: ids.length }));
      if (!proceed) return;
    }

    runBulkAction(() => bulkSetProductsStatus(ids, status));
  };

  const onBulkSetAnnouncement = (announcementId: string | null) => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setPendingAnnouncementId(announcementId);
    setPendingUnavailable(null);
    setAnnouncementModalOpen(true);
  };

  const onBulkSetUnavailable = (unavailable: boolean) => {
    if (!selectedIds.size) return;
    setPendingAnnouncementId(null);
    setPendingUnavailable(unavailable);
    setAnnouncementModalOpen(true);
  };

  // Fires every remaining page in parallel and merges them into one state
  // update, instead of the normal one-page-at-a-time crawl - used only to
  // eagerly finish loading the catalog for an active search (see the
  // backstop effect below), where the count/result visibly ticking up page
  // by page reads as broken rather than as progress.
  const loadRestInParallel = (total: number) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    const requestId = requestIdRef.current;
    const startOffset = offsetRef.current;
    const endpoint = isSuperuser ? "/admin/products" : "/products";

    const offsets: number[] = [];
    for (let offset = startOffset; offset < total; offset += LIMIT) offsets.push(offset);

    Promise.all(
      offsets.map(offset => {
        const params = new URLSearchParams({ offset: String(offset), limit: String(LIMIT) });
        params.set("status", isSuperuser ? statusFilter : "published");
        params.set("unavailable", String(unavailableOnly));
        selectedBrandIds.forEach(id => params.append("brand_id", String(id)));
        selectedCategoryIds.forEach(id => params.append("category_id", String(id)));
        selectedAnnouncementIds.forEach(id => params.append("announcement_id", id));
        if (featuredOnly) params.set("featured", "true");
        return apiFetch(`${endpoint}?${params.toString()}`).then(res => {
          if (!res.ok) throw new Error("Could not load products");
          return res.json() as Promise<Page>;
        });
      })
    )
      .then(pages => {
        if (requestId !== requestIdRef.current) return;
        offsetRef.current = total;
        setProducts(prev => [...prev, ...pages.flatMap(page => page.items)]);
        setHasMore(false);
      })
      .catch(() => {
        if (requestId === requestIdRef.current) setError(t("products.couldNotLoadProducts"));
      })
      .finally(() => {
        if (requestId === requestIdRef.current) loadingRef.current = false;
      });
  };

  const fetchMore = () => {
    // react-infinite-scroll-component wires its own IntersectionObserver to
    // this function (via the `next` prop) and calls it directly whenever
    // the sentinel is visible - which it always is while the page is still
    // empty. That fires independently of the effects below, so the
    // filtersReady gate has to live here too, not just at their call sites,
    // or an unresolved brand/category URL param gets raced by this firing
    // unfiltered before the filter has a chance to apply.
    if (!filtersReady || loadingRef.current || reloadPendingRef.current) return;
    loadingRef.current = true;
    // Captured so a response can tell it was superseded by a reset (tab
    // switch, auth resolving, etc.) before applying stale data.
    const requestId = requestIdRef.current;
    const offset = offsetRef.current;
    const endpoint = isSuperuser ? "/admin/products" : "/products";
    const params = new URLSearchParams({ offset: String(offset), limit: String(LIMIT) });
    params.set("status", isSuperuser ? statusFilter : "published");
    params.set("unavailable", String(unavailableOnly));
    selectedBrandIds.forEach(id => params.append("brand_id", String(id)));
    selectedCategoryIds.forEach(id => params.append("category_id", String(id)));
    selectedAnnouncementIds.forEach(id => params.append("announcement_id", id));
    if (featuredOnly) params.set("featured", "true");

    apiFetch(`${endpoint}?${params.toString()}`)
      .then(res => {
        if (!res.ok) throw new Error("Could not load products");
        return res.json();
      })
      .then((page: Page) => {
        if (requestId !== requestIdRef.current) return;
        offsetRef.current = offset + page.items.length;
        setProducts(prev => [...prev, ...page.items]);
        setTotal(page.total);
        setHasMore(page.has_more);
      })
      .catch(() => {
        if (requestId === requestIdRef.current) setError(t("products.couldNotLoadProducts"));
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          loadingRef.current = false;
          if (offset === 0) setIsInitialLoading(false);
        }
      });
  };

  // Bumps the request id (invalidating any in-flight fetch for the old
  // filter/page) and clears the loading gate so the new fetch below isn't
  // blocked by a still-resolving stale one.
  const reloadProducts = () => {
    requestIdRef.current += 1;
    loadingRef.current = false;
    offsetRef.current = 0;
    setIsInitialLoading(true);
    setProducts([]);
    setHasMore(true);
    fetchMore();
  };

  useEffect(() => {
    if (restoredSnapshot && taxonomyVersion === 0) return;
    Promise.all([
      apiFetch("/brands?limit=100")
        .then(res => res.json())
        .then((page: OptionsPage<Brand>) => setBrands(page.items))
        .catch(() => {}),
      apiFetch("/categories?limit=100")
        .then(res => res.json())
        .then((page: OptionsPage<Category>) => setCategories(page.items))
        .catch(() => {}),
      listAnnouncements()
        .then(setAnnouncements)
        .catch(() => {}),
    ]).finally(() => setOptionsLoaded(true));
  }, [taxonomyVersion, announcementModalOpen]);

  // Lets a link like the home page's brand tiles (/products?brand=Herbacol)
  // land with that filter already applied. Runs once the brand/category
  // lists are in, so it never overwrites a filter the user picks afterwards
  // by hand.
  useEffect(() => {
    if (restoredSnapshot) return;
    if (!optionsLoaded) return;
    const brandParam = searchParams.get("brand");
    const categoryParam = searchParams.get("category");

    if (brandParam) {
      const match = brands.find(brand => normalize(brand.name).includes(normalize(brandParam)));
      if (match) setSelectedBrandIds(new Set([match.id]));
    }
    if (categoryParam) {
      const match = categories.find(category => normalize(category.name).includes(normalize(categoryParam)));
      if (match) setSelectedCategoryIds(new Set([match.id]));
    }
    setFiltersReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsLoaded]);

  // Lets a link like the home page's shampoo/exfoliante tiles
  // (/products?search=Shampoos) land with that word already typed into the
  // search box - there's no matching category for those yet, so this is a
  // plain text search rather than a brand/category filter.
  useEffect(() => {
    if (restoredSnapshot) return;
    const searchParam = searchParams.get("search");
    if (searchParam) setSearch(searchParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Serialized so the reload effect below can compare filter selections by
  // value - the Set objects themselves only change reference when onChange
  // swaps them, which already works, but a primitive key keeps the
  // dependency array honest about what it's actually reacting to.
  const brandFilterKey = useMemo(() => [...selectedBrandIds].sort((a, b) => a - b).join(","), [selectedBrandIds]);
  const categoryFilterKey = useMemo(() => [...selectedCategoryIds].sort((a, b) => a - b).join(","), [selectedCategoryIds]);
  const announcementFilterKey = useMemo(() => [...selectedAnnouncementIds].sort().join(","), [selectedAnnouncementIds]);

  useEffect(() => {
    if (restoringInitialStateRef.current) {
      const timeout = setTimeout(() => {
        restoringInitialStateRef.current = false;
      }, 0);
      return () => clearTimeout(timeout);
    }
    if (authLoading || !filtersReady) return;
    // Debounced so toggling several filter checkboxes in quick succession
    // fires one request instead of one per click. Not required for
    // correctness - reloadProducts already invalidates any in-flight fetch
    // via requestIdRef - just avoids wasted round-trips.
    reloadPendingRef.current = true;
    const timeout = setTimeout(() => {
      reloadPendingRef.current = false;
      clearSelection();
      reloadProducts();
    }, 200);
    return () => {
      reloadPendingRef.current = false;
      clearTimeout(timeout);
    };
  }, [statusFilter, authLoading, isSuperuser, brandFilterKey, categoryFilterKey, announcementFilterKey, featuredOnly, unavailableOnly, filtersReady, taxonomyVersion]);

  // Backstop for react-infinite-scroll-component's IntersectionObserver gap -
  // see getScrollMarginPx. Runs after every products update (each successful
  // page load) and, if the loaded content still doesn't reach past the
  // threshold margin, fetches the next page directly instead of waiting for
  // a scroll-driven crossing event that may never happen.
  useEffect(() => {
    // Same gate as the reload effect above - an empty product list never
    // fills the viewport, so without this an unresolved brand/category URL
    // param got raced by this backstop firing an unfiltered fetchMore()
    // before the filtered reload ever got a chance to run.
    if (!hasMore || loadingRef.current || !filtersReady) return;

    // Search only narrows what's already been paginated in - there's no
    // backend search endpoint - so while a query is active, keep loading
    // the rest of the catalog regardless of viewport fit. Otherwise a
    // search typed right after landing on the page (before enough pages
    // loaded to fill the screen) reports a short, misleading result/count
    // even though more matches exist a page or two further in. Once `total`
    // is known (the first page has landed), grab everything left over in
    // one parallel batch instead of paging through it one round trip at a
    // time - both faster and avoids the result visibly ticking up page by
    // page while the user is mid-search.
    if (search.trim()) {
      if (total > offsetRef.current) loadRestInParallel(total);
      return;
    }

    const distanceToBottom = document.documentElement.scrollHeight - window.scrollY - window.innerHeight;
    if (distanceToBottom < getScrollMarginPx(SCROLL_THRESHOLD)) fetchMore();
  }, [products, hasMore, filtersReady, search, total]);

  const reachedEnd = !unavailableOnly && !hasMore && !isInitialLoading && filtersReady;

  useEffect(() => {
    if (!reachedEnd) {
      setUnavailableProducts([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const items: Product[] = [];
      for (let offset = 0; ; offset += 100) {
        const params = new URLSearchParams({
          offset: String(offset),
          limit: "100",
          status: isSuperuser ? statusFilter : "published",
          unavailable: "true",
        });
        selectedBrandIds.forEach(id => params.append("brand_id", String(id)));
        selectedCategoryIds.forEach(id => params.append("category_id", String(id)));
        selectedAnnouncementIds.forEach(id => params.append("announcement_id", id));
        if (featuredOnly) params.set("featured", "true");
        const res = await apiFetch(`${isSuperuser ? "/admin/products" : "/products"}?${params.toString()}`);
        if (!res.ok) throw new Error("Could not load products");
        const page: Page = await res.json();
        items.push(...page.items);
        if (!page.has_more) break;
      }
      if (!cancelled) setUnavailableProducts(items);
    })().catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reachedEnd, isSuperuser, statusFilter, brandFilterKey, categoryFilterKey, announcementFilterKey, featuredOnly]);

  // Search only narrows what's already been loaded by the infinite scroll -
  // there's no backend search endpoint to query the full catalog.
  const matchesSearch = (product: Product, query: string) =>
    pickLocalized(product.name, product.name_en, currentLanguage).toLowerCase().includes(query) ||
    (product.variant_skus ?? []).some(sku => sku.toLowerCase().includes(query));

  const visibleProducts = useMemo(() => {
    const query = search.trim().toLowerCase();
    return query ? products.filter(product => matchesSearch(product, query)) : products;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, search, currentLanguage]);

  const visibleUnavailable = useMemo(() => {
    const query = search.trim().toLowerCase();
    return query
      ? unavailableProducts.filter(product => matchesSearch(product, query))
      : unavailableProducts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unavailableProducts, search, currentLanguage]);

  // Shared with home/detail via react-query, so pictures already resolved
  // there (or on an earlier page here) render immediately. During search,
  // only resolve images for matching cards rather than the full catalog that
  // loadRestInParallel has fetched in the background.
  const visibleVariantIds = useMemo(
    () =>
      [...visibleProducts, ...(unavailableOpen ? visibleUnavailable : [])].flatMap(
        product => product.variant_ids
      ),
    [visibleProducts, visibleUnavailable, unavailableOpen]
  );
  const imageUrls = useVariantImagesMap(visibleVariantIds);

  useEffect(() => {
    if (!restoredSnapshot) return;
    const frame = requestAnimationFrame(() => {
      window.scrollTo({ top: restoredSnapshot.scrollY, behavior: "auto" });
      clearProductOrigin();
    });
    return () => cancelAnimationFrame(frame);
  }, [restoredSnapshot]);

  const rememberListPosition = () => {
    rememberProductOrigin(currentPath, window.scrollY, {
      products,
      total,
      hasMore,
      offset: offsetRef.current,
      statusFilter,
      search,
      selectedBrandIds: [...selectedBrandIds],
      selectedCategoryIds: [...selectedCategoryIds],
      selectedAnnouncementIds: [...selectedAnnouncementIds],
      featuredOnly,
      unavailableOnly,
      brands,
      categories,
      announcements,
      optionsLoaded,
      filtersReady,
      scrollY: window.scrollY,
      visibleVariantIndexes,
    } satisfies ProductListSnapshot);
  };

  // `inUnavailableSection` items carry only the unavailable variants, so they
  // get their own carousel-index key and aren't selectable (the bulk modal
  // works on the main list's variants).
  const renderProductItem = (product: Product, inUnavailableSection = false) => {
  const isSelectable = isSuperuser && selectMode && !inUnavailableSection;
  const indexKey = inUnavailableSection ? `unavailable:${product.id}` : product.id;
  const showAsUnavailable = unavailableOnly || inUnavailableSection;
  const visibleVariantIndex = visibleVariantIndexes[indexKey] ?? 0;
  const visibleVariantId = product.variant_ids[visibleVariantIndex];
  const announcementByVariantId = new Map(
    (product.variant_announcements ?? []).map(variant => [
      variant.id,
      variant.announcement,
    ])
  );
  const card = (
    <ProductCard
      name={pickLocalized(product.name, product.name_en, currentLanguage)}
      brand={product.brand.name}
      status={showAsUnavailable ? "unavailable" : product.status.name}
      announcement={product.announcement}
      imageUrls={product.variant_ids.map(id => imageUrls[id])}
      variantAnnouncements={product.variant_ids.map(
        id => announcementByVariantId.get(id) ?? null
      )}
      selected={selectedIds.has(product.id)}
      onToggleSelect={isSelectable ? () => toggleSelected(product.id) : undefined}
      initialVariantIndex={visibleVariantIndex}
      onVisibleVariantChange={index =>
        setVisibleVariantIndexes(previous => ({
          ...previous,
          [indexKey]: index,
        }))
      }
    />
  );

  return (
    <li key={indexKey} className="w-full min-[360px]:w-[calc(50%-0.375rem)] sm:w-55">
      {isSelectable ? (
        <div
          role="button"
          tabIndex={0}
          className="w-full cursor-pointer"
          onClick={() => toggleSelected(product.id)}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") toggleSelected(product.id);
          }}
        >
          {card}
        </div>
      ) : (
        <Link
          to={`/product/${product.slug}/p/${product.id}${
            visibleVariantId === undefined ? "" : `?variant=${visibleVariantId}`
          }`}
          className="block w-full"
          onClick={rememberListPosition}
          draggable={false}
        >
          {card}
        </Link>
      )}
    </li>
  );
  };

  if (error) return <div>{error}</div>;

  const selectedCount = selectedIds.size;
  const hasActiveFilters =
    selectedBrandIds.size > 0 ||
    selectedCategoryIds.size > 0 ||
    selectedAnnouncementIds.size > 0 ||
    featuredOnly ||
    unavailableOnly;
  const displayedProductCount = search.trim() ? visibleProducts.length : total;

  return (
    <div className="max-w-[1280px] mx-auto w-full pb-10">
      <div className="flex flex-col items-center gap-4 py-6">
                <div className="h-5 text-sm text-app-muted" aria-live="polite">
          {!isInitialLoading && displayedProductCount > 0 && (
            <span className="products-count-enter inline-block">
              {t("products.productsCount", { count: displayedProductCount })}
            </span>
          )}
        </div>
        <div className="relative w-full max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-4 w-4 text-app-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder={t("products.searchPlaceholder")}
            className="block w-full pl-9 pr-3 py-2 border border-app-border rounded-full text-base sm:text-sm bg-app-control text-foreground shadow-sm placeholder:text-app-muted focus:outline-none sm:focus:ring-1 sm:focus:ring-brand sm:focus:border-brand"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div className="flex flex-col items-center gap-6">
          <div className="flex flex-col items-center gap-2">
            <div className="flex flex-wrap items-center justify-center gap-2">
              <MultiSelectFilter
                label={t("products.filterBrand")}
                options={brands.map(brand => ({ id: brand.id, label: brand.name }))}
                selectedIds={selectedBrandIds}
                onChange={setSelectedBrandIds}
              />
              <MultiSelectFilter
                label={t("products.filterCategory")}
                options={categories.map(category => ({
                  id: category.id,
                  label: pickLocalized(category.name, category.name_en, currentLanguage),
                }))}
                selectedIds={selectedCategoryIds}
                onChange={setSelectedCategoryIds}
              />
              <MultiSelectFilter
                label={t("products.filterAnnouncement")}
                options={announcements.map(announcement => ({
                  id: announcement.id,
                  label: pickLocalized(announcement.message, announcement.message_en, currentLanguage),
                }))}
                selectedIds={selectedAnnouncementIds}
                onChange={setSelectedAnnouncementIds}
                mobileAlign="right"
              />
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              aria-pressed={featuredOnly}
              onClick={() => setFeaturedOnly(previous => !previous)}
              className={`group flex cursor-pointer items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
                featuredOnly
                  ? "border-brand bg-brand/10 text-brand"
                  : "border-app-border bg-app-control text-foreground hover:bg-app-control-hover"
              }`}
            >
              <SelectionCircle checked={featuredOnly} />
              <span>{t("products.filterFeatured")}</span>
            </button>
            <button
              type="button"
              aria-pressed={unavailableOnly}
              onClick={() => setUnavailableOnly(previous => !previous)}
              className={`group flex cursor-pointer items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
                unavailableOnly
                  ? "border-brand bg-brand/10 text-brand"
                  : "border-app-border bg-app-control text-foreground hover:bg-app-control-hover"
              }`}
            >
              <SelectionCircle checked={unavailableOnly} />
              <span>{t("products.statusUnavailable")}</span>
            </button>
            </div>
          </div>
          <button
            type="button"
            aria-hidden={!hasActiveFilters}
            className={`text-sm text-app-muted hover:text-foreground underline underline-offset-2 cursor-pointer ${hasActiveFilters ? "" : "invisible"}`}
            onClick={() => {
              setSelectedBrandIds(new Set());
              setSelectedCategoryIds(new Set());
              setSelectedAnnouncementIds(new Set());
              setFeaturedOnly(false);
              setUnavailableOnly(false);
            }}
          >
            {t("products.clearFilters")}
          </button>
        </div>

        {isSuperuser && (
          <>
            <Link to="/products/new" className="rounded-full px-4 py-1.5 font-medium text-white bg-brand hover:bg-brand/90 transition-colors duration-200">
              {t("products.addNewProduct")}
            </Link>
            <div role="tablist" className="inline-flex items-center gap-1 bg-app-surface border border-app-border rounded-lg shadow-sm p-1">
              {STATUS_TABS.map(tab => (
                <button
                  key={tab.value}
                  type="button"
                  role="tab"
                  aria-selected={statusFilter === tab.value}
                  className={`px-4 py-1.5 rounded-md cursor-pointer text-sm font-semibold transition-colors ${
                    statusFilter === tab.value ? "bg-brand text-white" : "text-app-muted hover:bg-app-surface-hover"
                  }`}
                  onClick={() => setStatusFilter(tab.value)}
                >
                  {t(tab.labelKey)}
                </button>
              ))}
            </div>
            <button
              type="button"
              className={`btn btn-sm border ${
                selectMode
                  ? "bg-brand border-brand text-white hover:bg-brand/90"
                  : "bg-app-control border-app-border text-foreground hover:bg-app-control-hover"
              }`}
              onClick={toggleSelectMode}
            >
              {selectMode ? t("products.doneSelecting") : t("products.selectProducts")}
            </button>
          </>
        )}
      </div>
      {isSuperuser && selectMode && (
        <div className="sticky top-24 z-20 flex justify-center px-4 py-3 bg-app-surface border border-app-border rounded-2xl shadow-sm mx-auto max-w-fit">
          <div className="flex flex-wrap items-center justify-center gap-2 text-foreground">
            <span className="text-sm font-medium min-w-28 text-center">
              {selectedCount > 0 ? t("products.selectedCount", { count: selectedCount }) : t("products.selectProductsBelow")}
            </span>
            {selectedCount > 0 && (
              <>
                {STATUS_TABS.filter(tab => tab.value !== statusFilter).map(tab => (
                  <button
                    key={tab.value}
                    type="button"
                    className="btn btn-sm bg-app-control border border-app-border text-foreground hover:bg-app-control-hover"
                    disabled={bulkActionPending}
                    onClick={() => onBulkSetStatus(tab.value)}
                  >
                    {t("products.moveTo", { status: t(tab.labelKey) })}
                  </button>
                ))}
                <AnnouncementPicker
                  value={null}
                  onChange={onBulkSetAnnouncement}
                  placeholder={t("products.setAnnouncement")}
                  noneLabel={t("products.clearAnnouncement")}
                  disabled={bulkActionPending}
                  className="btn btn-sm bg-app-control border border-app-border text-foreground hover:bg-app-control-hover"
                />
                <VariantAvailabilityPicker
                  onChange={onBulkSetUnavailable}
                  disabled={bulkActionPending}
                  className="btn btn-sm bg-app-control border border-app-border text-foreground hover:bg-app-control-hover"
                />
                <button
                  type="button"
                  className="btn btn-sm btn-error text-white"
                  disabled={bulkActionPending}
                  onClick={onBulkDelete}
                >
                  {t("common.delete")}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-ghost text-app-muted"
                  disabled={bulkActionPending}
                  onClick={clearSelection}
                >
                  {t("products.clearSelection")}
                </button>
              </>
            )}
          </div>
        </div>
      )}
      <VariantAnnouncementModal
        open={announcementModalOpen}
        announcementId={pendingAnnouncementId}
        unavailable={pendingUnavailable ?? undefined}
        products={products
          .filter(product => selectedIds.has(product.id))
          .map(product => ({
            key: product.id,
            name: pickLocalized(product.name, product.name_en, currentLanguage),
            variants: product.variant_ids.map((variantId, index) => ({
              key: String(variantId),
              imageVariantId: variantId,
              announcement: product.variant_announcements?.find(v => v.id === variantId)?.announcement ?? null,
              unavailable: product.variant_announcements?.find(v => v.id === variantId)?.is_unavailable ?? false,
              label: product.variant_names?.[index]
                ? pickLocalized(
                    product.variant_names[index],
                    product.variant_names_en?.[index],
                    currentLanguage
                  )
                : t("announcementAssignment.variantNumber", { number: index + 1 }),
            })),
          }))}
        saving={bulkActionPending}
        onClose={() => setAnnouncementModalOpen(false)}
        onSave={async (selectedVariantKeys, announcementId, allVariantKeys) => {
          const selectedVariantIds = selectedVariantKeys.map(Number);
          if (pendingUnavailable !== null) {
            const saved = await runBulkAction(() =>
              bulkSetVariantsUnavailable(selectedVariantIds, pendingUnavailable)
            );
            if (saved) {
              await queryClient.invalidateQueries({ queryKey: ["products", "announced"] });
              setAnnouncementModalOpen(false);
            }
            return;
          }
          const selectedVariantIdSet = new Set(selectedVariantIds);
          // Only unassign unchecked variants that currently carry this same
          // announcement. Variants holding a different one must be left alone,
          // otherwise assigning B after A would wipe A.
          const currentAnnouncementByVariantId = new Map<number, string | null>();
          for (const product of products) {
            for (const variant of product.variant_announcements ?? []) {
              currentAnnouncementByVariantId.set(variant.id, variant.announcement?.id ?? null);
            }
          }
          const clearVariantIds = announcementId
            ? allVariantKeys
                .map(Number)
                .filter(
                  variantId =>
                    !selectedVariantIdSet.has(variantId) &&
                    currentAnnouncementByVariantId.get(variantId) === announcementId
                )
            : [];
          const saved = await runBulkAction(() =>
            bulkSetVariantsAnnouncement(
              selectedVariantIds,
              announcementId,
              clearVariantIds
            )
          );
          if (saved) {
            await queryClient.invalidateQueries({ queryKey: ["products", "announced"] });
            setAnnouncementModalOpen(false);
          }
        }}
      />
      {isInitialLoading ? (
        <ul className="flex flex-wrap justify-center gap-3 py-10 sm:gap-10">
          {Array.from({ length: INITIAL_SKELETON_COUNT }, (_, i) => (
            <li key={i} className="w-full min-[360px]:w-[calc(50%-0.375rem)] sm:w-55">
              <ProductCardSkeleton />
            </li>
          ))}
        </ul>
      ) : (
        <InfiniteScroll
          dataLength={products.length}
          next={fetchMore}
          hasMore={hasMore}
          // Must be a bare number or a "NN%"/"NNpx" string - anything else
          // (e.g. "0.6") fails the library's validation silently and falls
          // back to its default.
          scrollThreshold={SCROLL_THRESHOLD}
          loader={
            <ul className="flex flex-wrap justify-center gap-3 py-10 sm:gap-10">
              {Array.from(
                // Capped small on purpose - with a big lookahead margin (see
                // SCROLL_THRESHOLD) the next page can fetch long before the
                // user is anywhere near it, so a full page's worth of
                // skeletons would just be extra placeholders to scroll past.
                { length: Math.min(2, total - products.length) },
                (_, i) => (
                  <li key={i} className="w-full min-[360px]:w-[calc(50%-0.375rem)] sm:w-55">
                    <ProductCardSkeleton />
                  </li>
                )
              )}
            </ul>
          }
        >
          <ul className="products-results-enter flex flex-wrap justify-center gap-3 py-10 sm:gap-10">
            {visibleProducts.map(product => renderProductItem(product))}
          </ul>
        </InfiniteScroll>
      )}
      {reachedEnd && visibleUnavailable.length > 0 && (
        <section className="px-4 pt-4">
          <button
            type="button"
            aria-expanded={unavailableOpen}
            onClick={() => setUnavailableOpen(open => !open)}
            className="group flex w-full cursor-pointer items-center gap-4 text-sm font-semibold text-brand transition-[filter] duration-300 ease-in-out hover:brightness-125 motion-reduce:transition-none"
          >
            <span className="h-px flex-1 bg-brand/40 transition-colors duration-300 ease-in-out group-hover:bg-brand" />
            <span className="flex items-center gap-2 transition-transform duration-300 ease-in-out group-hover:-translate-y-0.5 motion-reduce:transition-none">
              {t("products.unavailableSection", { count: visibleUnavailable.length })}
              <ChevronDown
                size={18}
                className={`transition-transform duration-300 ease-in-out ${unavailableOpen ? "rotate-180" : ""}`}
              />
            </span>
            <span className="h-px flex-1 bg-brand/40 transition-colors duration-300 ease-in-out group-hover:bg-brand" />
          </button>
          {/* Grid-rows 0fr -> 1fr animates height without measuring content. */}
          <div
            aria-hidden={!unavailableOpen}
            inert={!unavailableOpen}
            className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none ${
              unavailableOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
            }`}
          >
            <div className="overflow-hidden">
              <ul className="flex flex-wrap justify-center gap-3 py-10 sm:gap-10">
                {visibleUnavailable.map(product => renderProductItem(product, true))}
              </ul>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
